import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { createStackNavigator } from '@react-navigation/stack';
import { CommonActions, useNavigation, useNavigationState } from '@react-navigation/native';

import SideMenu from './SideMenu';
import MissionsScreen from '../screens/MissionsScreen';
import ChatScreen from '../screens/ChatScreen';
import NotificationsScreen from '../screens/NotificationsScreen';
import ProfileScreen from '../screens/ProfileScreen';
import DashboardScreen from '../screens/DashboardScreen';
import LeaderboardScreen from '../screens/LeaderboardScreen';
import InvoicesScreen from '../screens/InvoicesScreen';
import PaymentMethodsScreen from '../screens/PaymentMethodsScreen';
import CartScreen from '../screens/CartScreen';
import MyInformationScreen from '../screens/MyInformationScreen';
import ChapterDetailScreen from '../screens/ChapterDetailScreen';
import SceneReaderScreen from '../screens/SceneReaderScreen';
import MissionChatScreen from '../screens/MissionChatScreen';
import QuizScreen from '../screens/QuizScreen';
import TaskDetailScreen from '../screens/TaskDetailScreen';
import MissionCardsScreen from '../screens/MissionCardsScreen';
import MissionDetailScreen from '../screens/MissionDetailScreen';
import TaskBoardScreen from '../screens/TaskBoardScreen';
import ChaptersScreen from '../screens/ChaptersScreen';
import UserStoriesScreen from '../screens/UserStoriesScreen';
import UserStoryDetailScreen from '../screens/UserStoryDetailScreen';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions } from '../context/MissionsContext';
import { useNotifications } from '../context/NotificationsContext';

const InnerStack = createStackNavigator();

const MISSIONS_DETAIL_SCREENS = [
  'ChapterDetail', 'SceneReader', 'MissionChat', 'Quiz', 'TaskDetail',
  'MissionCards', 'MissionDetail', 'TaskBoard', 'Chapters',
  'UserStories', 'UserStoryDetail',
];

function InnerNavigator() {
  return (
    <InnerStack.Navigator screenOptions={{ headerShown: false }}>
      <InnerStack.Screen name="Missions"        component={MissionsScreen} />
      <InnerStack.Screen name="Chat"            component={ChatScreen} />
      <InnerStack.Screen name="Dashboard"       component={DashboardScreen} />
      <InnerStack.Screen name="Leaderboard"     component={LeaderboardScreen} />
      <InnerStack.Screen name="Invoices"        component={InvoicesScreen} />
      <InnerStack.Screen name="PaymentMethods"  component={PaymentMethodsScreen} />
      <InnerStack.Screen name="Account"         component={ProfileScreen} />
      <InnerStack.Screen name="MyInformation"   component={MyInformationScreen} />
      <InnerStack.Screen name="Notifications"   component={NotificationsScreen} />
      <InnerStack.Screen name="Cart"            component={CartScreen} />
      <InnerStack.Screen name="ChapterDetail"   component={ChapterDetailScreen} />
      <InnerStack.Screen name="SceneReader"     component={SceneReaderScreen} />
      <InnerStack.Screen name="MissionChat"     component={MissionChatScreen} />
      <InnerStack.Screen name="Quiz"            component={QuizScreen} />
      <InnerStack.Screen name="TaskDetail"      component={TaskDetailScreen} />
      <InnerStack.Screen name="MissionCards"    component={MissionCardsScreen} />
      <InnerStack.Screen name="MissionDetail"   component={MissionDetailScreen} />
      <InnerStack.Screen name="TaskBoard"       component={TaskBoardScreen} />
      <InnerStack.Screen name="Chapters"        component={ChaptersScreen} />
      <InnerStack.Screen name="UserStories"     component={UserStoriesScreen} />
      <InnerStack.Screen name="UserStoryDetail" component={UserStoryDetailScreen} />
    </InnerStack.Navigator>
  );
}

function DesktopHeader() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { cartItems } = useMissions();
  const { unread } = useNotifications();

  const currentRouteName = useNavigationState(state => {
    const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
    const innerState = mainRoute?.state;
    if (innerState) {
      return innerState.routes?.[innerState.index ?? 0]?.name ?? 'Missions';
    }
    return 'Missions';
  });

  return (
    <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
      <View style={styles.headerSpacer} />

      <TouchableOpacity
        onPress={() => navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: 'Main', state: { routes: [{ name: 'Notifications' }], index: 0 } }],
          })
        )}
        style={[styles.headerBtn, currentRouteName === 'Notifications' && { backgroundColor: colors.tabActiveBackground }]}
        activeOpacity={0.7}
      >
        <Ionicons
          name="notifications-outline"
          size={24}
          color={currentRouteName === 'Notifications' ? colors.tabActive : colors.iconPrimary}
        />
        {unread > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{unread}</Text>
          </View>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        onPress={() => navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: 'Main', state: { routes: [{ name: 'Cart' }], index: 0 } }],
          })
        )}
        style={[styles.headerBtn, currentRouteName === 'Cart' && { backgroundColor: colors.tabActiveBackground }]}
        activeOpacity={0.7}
      >
        <Ionicons
          name="cart-outline"
          size={24}
          color={currentRouteName === 'Cart' ? colors.tabActive : colors.iconPrimary}
        />
        {cartItems.length > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{cartItems.length}</Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );
}

export default function DesktopLayout() {
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  return (
    <View style={[styles.wrapper, { backgroundColor: colors.background }]}>
      <DesktopHeader />
      <View style={styles.container}>
        <SideMenu missionDetailScreens={MISSIONS_DETAIL_SCREENS} />
        <View style={[styles.content, { backgroundColor: colors.background }]}>
          <InnerNavigator />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: { flex: 1, flexDirection: 'column' },
  header: { height: 52, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingHorizontal: 16, borderBottomWidth: 1, gap: 4 },
  headerSpacer: { flex: 1 },
  headerBtn: { position: 'relative', padding: 8, borderRadius: 8 },
  badge: { position: 'absolute', top: 4, right: 4, backgroundColor: '#EF4444', borderRadius: 10, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  badgeText: { color: '#FFFFFF', fontSize: 11, fontWeight: 'bold' },
  container: { flex: 1, flexDirection: 'row' },
  content: { flex: 1, overflow: 'hidden' },
});
